import * as PIXI from "pixi.js";
import data from "../../configs/data";
import {Button} from "../../controlElements/Button";
import {CustomSpineAnimation} from "../../CustomSpineAnimation";
import {IBetPicker} from "./IBetPicker";

export class BetPicker extends PIXI.Container implements IBetPicker {

    private readonly ANIMATION_CONFIG = {
        NAME: "betPicker",
        STATES: {
            IDLE: "idle",
            ROTATE_LEFT: "rotate_left",
            ROTATE_RIGHT: "rotate_right",
        },
        SLOT_CONTAINERS: {
            TOKENS_ARRAY: [
                "token1",
                "token2",
                "token3",
                "token4",
                "token5",
                "token6",
            ],
        },
    };

    private betPickerRotationAnimation: CustomSpineAnimation;
    private subscribers: Array<Function> = [];
    private buttons: Array<Button> = [];
    private invisiblePreviousButton: Button;
    private invisibleNextButton: Button;

    private currentValue = 1;
    private currentButtonIndex = 0;

    private readonly BACKGROUND_POSITION = new PIXI.Point(-201, -182);
    private readonly INVISIBLE_PREVIOUS_BUTTON_POSITION = new PIXI.Point(-115, -100);
    private readonly INVISIBLE_NEXT_BUTTON_POSITION = new PIXI.Point(115, -100);
    private readonly CHIPS_AMOUNT = 6;

    constructor(betPickerValuesPaths: Array<string>, private betPickerValues: Array<number>) {
        super();
        if (betPickerValues.length !== this.CHIPS_AMOUNT || betPickerValuesPaths.length !== this.CHIPS_AMOUNT) {
            throw new Error("Wrong amount of values passed!");
        }

        this.createAndAddBackground();
        this.createAndAddInvisiblePreviousButton();
        this.createAndAddInvisibleNextButton();
        this.createAndAddRotationAnimation();
        this.createAndAddBetButtons(betPickerValuesPaths, betPickerValues);
        this.betPickerRotationAnimation.play(this.ANIMATION_CONFIG.STATES.IDLE);
    }

    public subscribeOnBetChanged(subscriber: Function): void {
        this.subscribers.push(subscriber);
        this.onValueChosen();
    }

    public getBetValue(): number {
        return this.currentValue;
    }

    private onValueChosen(): void {
        this.currentValue = this.betPickerValues[this.currentButtonIndex];
        for (let i = 0; i < this.subscribers.length; i++) {
            this.subscribers[i](this.currentValue);
        }
    }

    private createAndAddBetButtons(betPickerValuesPaths: Array<string>, betPickerValues: Array<number>): void {
        let button;
        for (let i = 0; i < betPickerValues.length; i++) {
            button = new Button(betPickerValuesPaths[i], betPickerValuesPaths[i],
                betPickerValuesPaths[i], betPickerValuesPaths[i], data.audios.buttonRotaryClicked);
            button.subscribeOnClicked(() => {
                if (this.currentButtonIndex === 0 && i === (this.CHIPS_AMOUNT - 1)) {
                    this.onPreviousButtonClicked();
                } else if (this.currentButtonIndex === (this.CHIPS_AMOUNT - 1) && i === 0) {
                    this.onNextButtonClicked();
                } else if (this.currentButtonIndex > i) {
                    this.onPreviousButtonClicked();
                } else if (this.currentButtonIndex < i) {
                    this.onNextButtonClicked();
                } else if (this.currentButtonIndex === i) {
                    return;
                }

                this.disableAllButtons();
                this.onValueChosen();
            });
            button.scale.y = -1;
            this.buttons.push(button);
            this.betPickerRotationAnimation.getSlotContainer(
                this.ANIMATION_CONFIG.SLOT_CONTAINERS.TOKENS_ARRAY[i], true).addChild(button);
        }
    }

    private onNextButtonClicked(): void {
        this.betPickerRotationAnimation.play(this.ANIMATION_CONFIG.STATES.ROTATE_RIGHT);
        this.betPickerRotationAnimation.addOnCompleteListener(this.onRotationComplete.bind(this));
        this.currentButtonIndex++;
        if (this.currentButtonIndex === this.CHIPS_AMOUNT) {
            this.currentButtonIndex = 0;
        }
    }

    private onPreviousButtonClicked(): void {
        this.betPickerRotationAnimation.play(this.ANIMATION_CONFIG.STATES.ROTATE_LEFT);
        this.betPickerRotationAnimation.addOnCompleteListener(this.onRotationComplete.bind(this));
        this.currentButtonIndex--;
        if (this.currentButtonIndex === -1) {
            this.currentButtonIndex = (this.CHIPS_AMOUNT - 1);
        }
    }

    private enableAllButtons(): void {
        for (let i = 0; i < this.buttons.length; i++) {
            this.buttons[i].enabled = true;
        }
        this.invisiblePreviousButton.enabled = true;
        this.invisibleNextButton.enabled = true;
    }

    private disableAllButtons(): void {
        for (let i = 0; i < this.buttons.length; i++) {
            this.buttons[i].enabled = false;
        }
        this.invisiblePreviousButton.enabled = false;
        this.invisibleNextButton.enabled = false;
    }

    private createAndAddBackground(): void {
        const background: PIXI.Sprite = PIXI.Sprite.fromImage(data.images.betPickerPanelBackground);
        background.position = this.BACKGROUND_POSITION;
        this.addChild(background);
    }

    private createAndAddInvisiblePreviousButton(): void {
        this.invisiblePreviousButton = new Button(data.images.invisibleBetPickerButton,
            data.images.invisibleBetPickerButton, data.images.invisibleBetPickerButton,
            data.images.invisibleBetPickerButton, data.audios.buttonRotaryClicked);
        this.invisiblePreviousButton.position = this.INVISIBLE_PREVIOUS_BUTTON_POSITION;
        this.invisiblePreviousButton.subscribeOnClicked(() => {
            this.onPreviousButtonClicked();
            this.disableAllButtons();
            this.onValueChosen();
        });
        this.addChild(this.invisiblePreviousButton);
    }

    private createAndAddInvisibleNextButton(): void {
        this.invisibleNextButton = new Button(data.images.invisibleBetPickerButton,
            data.images.invisibleBetPickerButton, data.images.invisibleBetPickerButton,
            data.images.invisibleBetPickerButton, data.audios.buttonRotaryClicked);
        this.invisibleNextButton.position = this.INVISIBLE_NEXT_BUTTON_POSITION;
        this.invisibleNextButton.subscribeOnClicked(() => {
            this.onNextButtonClicked();
            this.disableAllButtons();
            this.onValueChosen();
        });
        this.addChild(this.invisibleNextButton);
    }

    private createAndAddRotationAnimation(): void {
        this.betPickerRotationAnimation = new CustomSpineAnimation(this.ANIMATION_CONFIG.NAME);
        this.addChild(this.betPickerRotationAnimation);
    }

    private onRotationComplete(): void {
        this.enableAllButtons();
        this.betPickerRotationAnimation.removeAllListenersMyCopy();
        this.betPickerRotationAnimation.play(this.ANIMATION_CONFIG.STATES.IDLE);
        for (let i = 0; i < this.CHIPS_AMOUNT; i++) {
            this.betPickerRotationAnimation.getSlotContainer(this.ANIMATION_CONFIG.SLOT_CONTAINERS.TOKENS_ARRAY[i])
                .addChild(this.buttons[(this.currentButtonIndex + i) % this.CHIPS_AMOUNT]);
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/userInterface/betPicker/BetPicker.ts